import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { MessageCircle, Send, RefreshCw } from "lucide-react";
import { useNotifications } from "../context/NotificationContext";
import { ChannelPicker, DEFAULT_CHANNELS, resultToast } from "../components/AdminChannelPicker";

const AdminThread = ({ thread, onSent }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [channels, setChannels] = useState(DEFAULT_CHANNELS);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const { items } = useNotifications();

  const load = useCallback(async () => {
    setLoading(true);
    try { setMessages((await api.get(`/admin/merchants/${thread.merchant_id}/messages`)).data); }
    catch (e) { toast.error(arabicError(e)); }
    finally { setLoading(false); }
  }, [thread.merchant_id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (items[0]?.type === "trader_message" && items[0]?.merchant_id === thread.merchant_id) load();
  }, [items, load, thread.merchant_id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || channels.length === 0) return;
    setSending(true);
    try {
      const r = await api.post(`/admin/merchants/${thread.merchant_id}/message`, { message: input.trim(), channels });
      setMessages((prev) => [...prev, { id: `local-${Date.now()}`, direction: "to_merchant", message: input.trim(), ts: new Date().toISOString() }]);
      setInput("");
      resultToast(toast, r.data);
      onSent?.();
    } catch (e) { toast.error(arabicError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="bg-white border border-[#7AB2B2] rounded-2xl flex flex-col h-[70vh]" data-testid="admin-thread-panel">
      <div className="p-4 border-b border-[#EBF4F6]">
        <div className="font-extrabold text-[#333333]" data-testid="admin-thread-title">{thread.store_name}</div>
        <div className="text-xs text-[#666666]">{thread.merchant_name} · {thread.email}{thread.city ? ` · ${thread.city}` : ""}</div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="admin-thread-messages">
        {loading ? <div className="text-center text-[#666666] py-8">جارٍ التحميل...</div>
          : messages.map((m) => {
            const fromTrader = m.direction === "to_admin";
            return (
              <div key={m.id} className={`flex ${fromTrader ? "justify-end" : "justify-start"}`} data-testid={`admin-msg-${m.id}`}>
                <div className={`max-w-[80%] rounded-2xl p-3 ${fromTrader ? "bg-[#EBF4F6] text-[#333333]" : "bg-[#09637E] text-white"}`}>
                  <div className="text-xs font-bold opacity-80 mb-1">{fromTrader ? thread.store_name : "الإدارة"}</div>
                  <div className="whitespace-pre-wrap break-words">{m.message}</div>
                  <div className="text-[10px] opacity-70 mt-1">{m.ts ? new Date(m.ts).toLocaleString("ar-SY", { dateStyle: "short", timeStyle: "short" }) : ""}</div>
                </div>
              </div>
            );
          })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="border-t border-[#7AB2B2] p-3 space-y-2">
        <ChannelPicker value={channels} onChange={setChannels} testid="admin-thread-channels" />
        <div className="flex flex-wrap sm:flex-nowrap gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="admin-thread-input" placeholder="اكتب ردّك..."
          className="h-11 flex-1 min-w-0 rounded-lg border border-[#7AB2B2] px-3 focus:outline-none focus:ring-2 focus:ring-[#09637E]" />
        <button type="submit" disabled={sending || !input.trim() || channels.length === 0} data-testid="admin-thread-send"
          className="h-11 px-4 shrink-0 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold text-sm flex items-center gap-1 disabled:opacity-50">
          <Send size={15} /> إرسال
        </button>
        </div>
      </form>
    </div>
  );
};

const AdminMessages = () => {
  const [searchParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const { items, refresh } = useNotifications();

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/messages/threads");
      setThreads(r.data);
      const target = searchParams.get("merchant");
      if (target) setActive((cur) => cur || r.data.find((t) => t.merchant_id === target) || null);
    } catch (e) { toast.error(arabicError(e)); }
    finally { setLoading(false); }
  }, [searchParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (items[0]?.type === "trader_message") load(); }, [items, load]);

  const openThread = (t) => {
    setActive(t);
    setThreads((prev) => prev.map((x) => (x.merchant_id === t.merchant_id ? { ...x, unread: 0 } : x)));
    setTimeout(refresh, 800);
  };
  useEffect(() => { if (active) setTimeout(refresh, 800); }, [active, refresh]);

  return (
    <div className="space-y-6" data-testid="admin-messages-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#333333] flex items-center gap-2"><MessageCircle className="text-[#09637E]" size={32} /> الرسائل</h1>
          <p className="text-[#666666] mt-1">محادثات التجار مع الإدارة — اقرأ وردّ مباشرة من هنا</p>
        </div>
        <button onClick={load} data-testid="admin-messages-refresh" className="h-10 w-10 rounded-lg border border-[#7AB2B2] text-[#09637E] flex items-center justify-center hover:bg-[#EBF4F6]"><RefreshCw size={16} /></button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-[#7AB2B2] rounded-2xl overflow-hidden lg:max-h-[70vh] overflow-y-auto" data-testid="admin-threads-list">
          {loading ? <div className="p-6 text-center text-[#666666]">جارٍ التحميل...</div>
            : threads.length === 0 ? <div className="p-8 text-center text-[#666666]" data-testid="admin-threads-empty">لا توجد محادثات بعد</div>
            : threads.map((t) => (
              <button key={t.merchant_id} onClick={() => openThread(t)} data-testid={`admin-thread-${t.merchant_id}`}
                className={`w-full text-right p-4 border-b border-[#EBF4F6] hover:bg-[#EBF4F6] transition-colors ${active?.merchant_id === t.merchant_id ? "bg-[#EBF4F6]" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-extrabold text-[#333333] truncate">{t.store_name}{t.is_deleted ? " (محذوف)" : ""}</div>
                  {t.unread > 0 && <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-[11px] font-extrabold flex items-center justify-center" data-testid={`admin-thread-unread-${t.merchant_id}`}>{t.unread}</span>}
                </div>
                <div className="text-xs text-[#666666] truncate">{t.merchant_name}{t.city ? ` · ${t.city}` : ""}</div>
                <div className="text-sm text-[#333333] truncate mt-1">{t.last_from_trader ? "" : "أنت: "}{t.last_message}</div>
                <div className="text-[10px] text-[#666666] mt-0.5">{t.last_ts ? new Date(t.last_ts).toLocaleString("ar-SY", { dateStyle: "short", timeStyle: "short" }) : ""}</div>
              </button>
            ))}
        </div>
        <div className="lg:col-span-2">
          {active ? <AdminThread thread={active} onSent={load} />
            : <div className="bg-white border border-[#7AB2B2] rounded-2xl h-[70vh] flex items-center justify-center text-[#666666]" data-testid="admin-thread-placeholder">اختر محادثة من القائمة</div>}
        </div>
      </div>
    </div>
  );
};

export default AdminMessages;
